import { useState } from 'react'

import { startCase } from 'lodash'
import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert, SeverityPill, AssetDetailModal } from '@/components/ui'
import useCompany from '@/hooks/useCompany'
import locationDrilldown from '@/insights/common/alarmLocationDrilldown'
import { interval, validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import {
  Alarm,
  ApiService,
  AssetTypeFilter,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
  type InsightDefinition,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

export interface ReeferAlarmDetailsParams extends InsightParametersType {
  companyId?: number
  assetIds: number[]
  severity?: string[]
  geofenceIds?: number[]
  geofenceCategoryIds?: number[]
  codeId?: number[]
  date?: string
  from?: string
  to?: string
}

const ReeferAlarmActiveBySeverity: InsightDefinition<ReeferAlarmDetailsParams, Alarm[]> = {
  active: true,
  slug: 'alarm/details',
  category: 'reeferAlarm',
  name: 'Reefer Alarm Detail by Asset',
  description: 'Display detailed information about an alarm',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    // called after each parameter set; for this one we want to allow
    // either severity OR alarm code so clear one when the other changes.
    transform: ({
      parameters,
      field,
    }: {
      parameters: ReeferAlarmDetailsParams
      field: keyof ReeferAlarmDetailsParams
    }) => {
      if (field === 'codeId') {
        return { ...parameters, severity: undefined }
      }
      if (field === 'severity') {
        return { ...parameters, codeId: undefined }
      }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetIds', control: 'AssetDropdownMulti', required: true, assetType: AssetTypeFilter.InTransit },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alarm Code' },
      { id: 'codeId', control: 'AlarmCodeDropdown', required: false },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      { id: 'dateRange', control: 'DateTimeRange', dependsOn: 'date', dependsOnValue: 'custom' },
    ],

    isValid: (configuration: ReeferAlarmDetailsParams) => {
      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      const hasCodeId = configuration.codeId && configuration.codeId.length > 0
      const hasSeverity = configuration.severity && configuration.severity.length > 0
      const hasAssets = configuration.assetIds && configuration.assetIds.length > 0

      if (configuration?.companyId && hasAssets && (hasCodeId || hasSeverity)) {
        return true
      }
      if (configuration?.companyId) {
        if (hasAssets) {
          return 'Alarm Code or Severity is required'
        }
        return 'Asset and either Alarm Code or Severity is required'
      }
      return 'Company, Asset, and either Alarm Code or Severity are required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferAlarmDetailsParams) => {
    const path = `/insights/alarms/alarms-detail?${buildQuery(parameters)}`
    const response = await api.get<Alarm[]>({ path, origin: 'AlarmDetails' })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferAlarmDetailsParams, title: string) => {
    const path = '/insights/alarms/details'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    if (status.code === InsightStatusCode.LOADING) {
      // This could be handled by the framework by just returning the
      // same status. But by doing it here, we can make the skeleton
      // match the table which is cool.
      return <TableSkeleton columns={9} withHeader rowHeight={35} />
    }
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alarms found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const parameters = configuration.parameters as ReeferAlarmDetailsParams
    const hasGeofenceData = data?.some((alarm) => alarm.raisedGeofence)
    const body = data
      ?.sort((a, b) => {
        const t = b.raisedTime.getTime() - a.raisedTime.getTime()
        if (t === 0) {
          return a.asset.name.localeCompare(b.asset.name)
        }
        return t
      })
      .map((alarm) => {
        const rowData = {
          asset: (
            <button
              onClick={() => setSelectedAsset(alarm.asset.id)}
              className="text-left block text-blue-500 cursor-pointer"
            >
              {alarm.asset.name}
            </button>
          ),
          alarm: (
            <div className="text-left">
              {alarm.alarmCode.description} ({alarm.alarmCode.code})
            </div>
          ),
          raisedTime: (
            <div className="text-left">
              <span>{alarm.raisedTime ? DateTime.fromJSDate(alarm.raisedTime).toFormat('yyyy-MM-dd HH:mm') : ''}</span>
            </div>
          ),
          clearedTime: (
            <div className="text-left">
              <span>
                {alarm.clearedTime ? DateTime.fromJSDate(alarm.clearedTime).toFormat('yyyy-MM-dd HH:mm') : ''}
              </span>
            </div>
          ),
          duration: (
            <div className="text-left">
              <span>{interval(alarm.raisedTime, alarm.clearedTime)}</span>
            </div>
          ),
          severity: (
            <SeverityPill
              label={getSeverityLabel(String(alarm.alarmCode.severityCode))}
              severityCode={alarm.alarmCode.severityCode}
            />
          ),
          raisedLocation: locationDrilldown(alarm, configuration, 'raised', parameters.companyId),
          clearedLocation: locationDrilldown(alarm, configuration, 'cleared', parameters.companyId),
          ...(hasGeofenceData && {
            geofence: alarm.raisedGeofence ? alarm.raisedGeofence.name : '',
          }),
        }

        return rowData
      })
    const dataItem = body?.[0]

    const headers = dataItem
      ? Object.keys(dataItem).map((d) => ({ key: d, title: startCase(d) }))
      : Array(3)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as ReeferAlarmDetailsParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }

    if (params.severity?.length) {
      const labels = params.severity
        .map((s) => getSeverityLabel(s.toString()))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (params.assetIds?.length) {
      fields[''] = `${params.assetIds.length} Asset${params.assetIds.length === 1 ? '' : 's'}`
    }
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferAlarmActiveBySeverity
